import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import AddIcon from "@mui/icons-material/AddOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { JobForm } from "@/components/jobs/job-form";
import { type Job, useJobs } from "@/api/jobs";
import { usePipelines } from "@/api/pipelines";

export const Route = createFileRoute("/_app/jobs/")({
  component: JobsList,
});

function JobsList() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useJobs();
  const pipelines = usePipelines();
  const [form, setForm] = useState<{ open: boolean; job: Job | null }>({ open: false, job: null });

  const pipelineOptions = useMemo(
    () => (pipelines.data?.content ?? []).map((p) => ({ id: p.id, name: p.name })),
    [pipelines.data],
  );

  const columns: Column<Job>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "mode", header: "Mode", cell: (r) => r.executionMode ?? "SEQUENTIAL", nowrap: true },
    { id: "bindings", header: "Pipelines", align: "right", cell: (r) => r.bindings?.length ?? 0 },
    {
      id: "last",
      header: "Last run",
      cell: (r) => (r.lastRunStatus ? <StatusChip status={r.lastRunStatus} /> : <span>—</span>),
    },
    { id: "when", header: "When", cell: (r) => <RelativeTime value={r.lastRunAt} /> },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Jobs"
        description="File-batch jobs — match uploaded files to pipelines by glob pattern."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setForm({ open: true, job: null })}
          >
            New job
          </Button>
        }
      />
      <Card>
        <DataTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          search={(r) => r.name}
          searchPlaceholder="Search jobs…"
          onRowClick={(r) => navigate({ to: "/jobs/$jobId", params: { jobId: r.id } })}
          empty={
            <EmptyState
              title="No jobs yet"
              description="Create a job to route uploaded files to pipelines by glob pattern."
            />
          }
        />
      </Card>

      <JobForm
        open={form.open}
        onClose={() => setForm({ open: false, job: null })}
        job={form.job}
        pipelines={pipelineOptions}
      />
    </PageContainer>
  );
}
